import React, { useEffect, useState } from 'react';
import { useLazyQuery, useQuery } from '@common/hooks';
import { ErrorHandler, LoadingState } from '@common/components';
import { WorkerForm } from '@scheduling/components';
import { WorkerDetails } from './_worker-details';
import { Colors } from 'works/legacy/lib/style-helper';
import { setBreadcrumbs } from 'works/legacy/lib/client-store';
import {
  WorkerDetailQuery,
  WorksIntegrationConfigurationQuery,
} from '@scheduling/graphql/queries/worker-detail';

type Props = {
  workerId: string;
};

export function WorkerDetail({ workerId }: Props) {
  const [showDrawer, setShowDrawer] = useState(false);

  const { loading, error, data } = useQuery(WorkerDetailQuery, {
    variables: { id: workerId },
  });

  const [
    getIntegrationConfigData,
    { loading: integrationConfigLoading, data: integrationConfigData },
  ] = useLazyQuery(WorksIntegrationConfigurationQuery);

  useEffect(() => {
    if (data?.worker) {
      getIntegrationConfigData({ variables: { workerId: data.worker.id } });
    }
  }, [data?.worker, getIntegrationConfigData]);

  const integrationConfiguration =
    integrationConfigData?.integrationConfiguration;
  const workerIntegrationEnabled =
    integrationConfiguration?.workerIntegrationEnabled;
  const allowListSyncEnabled = integrationConfiguration?.allowListSyncEnabled;
  const editingAllowListsPermitted =
    integrationConfiguration?.editingAllowListsPermitted;
  const workerFound = integrationConfiguration?.workerFound;
  const integrationType = integrationConfiguration?.integrationType;
  const mappedHomeUnit = integrationConfiguration?.mappedHomeUnit;

  if (error) {
    return <ErrorHandler error={error} log="OnDemandTeamTable" />;
  }

  setBreadcrumbs([
    { name: 'Team', path: '/works/team/on_demand' },
    {
      name: loading ? '' : data?.worker.worksUser.name,
      path: `/works/team/on_demand/${workerId}`,
      privateInfo: true,
    },
  ]);

  return (
    <>
      {loading || !data ? (
        <LoadingState />
      ) : (
        <WorkerDetails
          setShowDrawer={setShowDrawer}
          worker={data.worker}
          workerIntegrationEnabled={workerIntegrationEnabled}
          workerFound={workerFound || undefined}
          mappedHomeUnit={mappedHomeUnit || undefined}
        />
      )}
      {showDrawer && data && (
        <WorkerForm
          setShowDrawer={setShowDrawer}
          worker={data.worker}
          workerIntegrationEnabled={workerIntegrationEnabled}
          allowListSyncEnabled={allowListSyncEnabled}
          editingAllowListsPermitted={editingAllowListsPermitted}
          workerFound={workerFound}
          integrationType={integrationType}
          integrationConfigLoading={integrationConfigLoading}
        />
      )}
      <style jsx>{`
        :global(.worker-detail-drawer .ant-drawer-body) {
          padding: 0;
          background-color: ${Colors.steelLighter};
        }
      `}</style>
    </>
  );
}
